import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth-context.js'
import { useMeTenant } from '../lib/me-api.js'
import { prefetchMyPlanUpgradeRequests } from '../lib/plan-upgrade-api.js'
import { IconPlan, IconSettings } from './SidebarIcons.js'
import { SidebarNavLink } from './SidebarNavLink.js'
import { SidebarSourceList } from './SidebarSourceList.js'
import { StorageMeter } from './StorageMeter.js'

type Props = {
  onNavigate?: (() => void) | undefined
}

export function TenantSidebar({ onNavigate }: Props) {
  const { session, signOut } = useAuth()
  const queryClient = useQueryClient()
  const meTenantQ = useMeTenant()
  const workspaceName = meTenantQ.data?.name ?? 'ワークスペース'
  const showUpgrade = meTenantQ.data?.plan !== 'EPHEMERAL'
  const showStorage = meTenantQ.data !== undefined && meTenantQ.data.plan !== 'EPHEMERAL'
  const isRegularTenant = meTenantQ.data?.kind === 'REGULAR'

  useEffect(() => {
    if (isRegularTenant) {
      void prefetchMyPlanUpgradeRequests(queryClient)
    }
  }, [isRegularTenant, queryClient])

  const prefetchUpgradeRequests = () => {
    if (isRegularTenant) {
      void prefetchMyPlanUpgradeRequests(queryClient)
    }
  }

  return (
    <div className="flex h-full flex-col bg-muted/30">
      <div className="shrink-0 px-3 pt-3 pb-2">
        <Link
          to="/projects"
          onClick={onNavigate}
          className="flex items-center gap-2.5 min-w-0 px-1"
        >
          <span
            aria-hidden
            className="inline-flex w-7 h-7 shrink-0 items-center justify-center rounded-md bg-gradient-primary text-white text-[10px] font-bold shadow-sm"
          >
            T
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-bold tracking-tight text-foreground truncate">
              Terafile Bin
            </p>
            <p className="text-[11px] text-muted-foreground truncate">{workspaceName}</p>
          </div>
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2 min-h-0">
        <SidebarSourceList {...(onNavigate ? { onNavigate } : {})} />
      </div>

      <div className="shrink-0 border-t border-border/60 px-2 py-2 space-y-0.5">
        <SidebarNavLink to="/settings" {...(onNavigate ? { onNavigate } : {})}>
          <IconSettings />
          設定
        </SidebarNavLink>
        {showUpgrade && (
          <SidebarNavLink
            to="/upgrade"
            {...(onNavigate ? { onNavigate } : {})}
            onPrefetch={prefetchUpgradeRequests}
          >
            <IconPlan />
            プラン変更
          </SidebarNavLink>
        )}
      </div>

      <div className="shrink-0 border-t border-border/60 p-3 space-y-2">
        {showStorage && meTenantQ.data && (
          <StorageMeter
            compact
            plan={meTenantQ.data.plan}
            addonStorageTb={meTenantQ.data.addonStorageTb}
            storageUsedBytes={meTenantQ.data.storageUsedBytes}
          />
        )}
        <div className="space-y-1.5">
          {session?.user.email && (
            <p className="text-[11px] text-muted-foreground truncate px-1">{session.user.email}</p>
          )}
          <button
            type="button"
            onClick={() => signOut()}
            className="w-full px-2.5 py-1.5 border border-border rounded-md text-[11px] font-medium hover:bg-accent transition-colors"
          >
            ログアウト
          </button>
        </div>
      </div>
    </div>
  )
}
